import { isValidElement, ReactNode } from 'react';

import { TableColumn } from 'components/Table';
import { ColumnIds } from 'enums';

import { convertToCsv } from './csv.utils';
import { getNestedValue } from './table.utils';

export const DEFAULT_EXCLUDED_EXPORT_COLUMN_IDS = [ColumnIds.Actions, ColumnIds.DateActions];

const isExcelDateColumn = (column: TableColumn): boolean =>
  column.type === 'date' ||
  column.type === 'datetime' ||
  column.filters?.some(filter => filter.type === 'date' || filter.type === 'datetime') === true;

// Excel auto-parses date-like CSV values as datetimes; narrow columns then show ########.
// A leading tab forces text mode so the value displays as written.
const formatCellForExcel = (value: string, column: TableColumn): string => {
  if (value === 'N/A' || !isExcelDateColumn(column)) {
    return value;
  }

  return `\t${value}`;
};

export const getExportableColumns = (
  columns: TableColumn[],
  excludeColumnIds: string[] = DEFAULT_EXCLUDED_EXPORT_COLUMN_IDS,
): TableColumn[] => columns.filter(column => !excludeColumnIds.includes(column.id));

export const stringifyCsvCellValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (isValidElement(value as ReactNode)) {
    return 'N/A';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

export const getTableExportCellValue = (row: object, column: TableColumn): string => {
  const nestedValue = getNestedValue(row, column.id);
  const rawValue =
    typeof nestedValue === 'object' && nestedValue !== null && 'value' in nestedValue
      ? nestedValue.value
      : nestedValue;

  if (column.exportFormatter) {
    return stringifyCsvCellValue(column.exportFormatter(rawValue, row));
  }

  if (column.formatter) {
    const formatted = column.formatter(rawValue, row);

    if (isValidElement(formatted as ReactNode)) {
      return stringifyCsvCellValue(rawValue);
    }

    return stringifyCsvCellValue(formatted);
  }

  return stringifyCsvCellValue(rawValue);
};

export const buildTableCsv = <T extends object>(
  rows: T[],
  columns: TableColumn[],
  excludeColumnIds?: string[],
): string => {
  const exportColumns = getExportableColumns(columns, excludeColumnIds);
  const headers = exportColumns.map(column => column.label);
  const csvRows = rows.map(row =>
    exportColumns.map(column => formatCellForExcel(getTableExportCellValue(row, column), column)),
  );

  return convertToCsv(headers, csvRows);
};
